import {
  pgTable,
  text,
  timestamp,
  uuid,
  integer,
  boolean,
  index,
  uniqueIndex,
  check,
  jsonb,
  time,
  date,
} from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";

const tz = { withTimezone: true } as const;

export interface WebinarReportPayload {
  webinarId: string;
  title: string;
  generatedAt: string;
  durationSeconds: number;
  attendanceFunnel: {
    registered: number;
    joined: number;
    stayed50Percent: number;
    stayedToEnd: number;
  };
  peakEngagementMoment: {
    timestamp: string | null;
    attendeeCount: number;
    chatCount: number;
    pollVotes: number;
    reactions: number;
    score: number;
    summary: string;
  } | null;
  ctaPerformance: Array<{
    ctaId: string;
    label: string;
    type: string;
    clicks: number;
    conversions: number;
    revenueCents: number;
  }>;
  topQuestions: Array<{
    id: string;
    question: string;
    askedBy: string;
    upvotes: number;
    answered: boolean;
  }>;
  moderationSummary?: {
    totalHeld: number;
    approved: number;
    rejected: number;
    undone: number;
    attendeeReports: {
      open: number;
      resolved: number;
      dismissed: number;
    };
    reasonCodeCounts: Record<string, number>;
    classifierFallbackTriggered: number;
    raidEpisodes: number;
    topActors: Array<{ actorId: string; actions: number }>;
  };
  recommendedFollowUps: string[];
}

// ---------------------------------------------------------------------------
// Webinar Series — Linked webinars as a course/program
// ---------------------------------------------------------------------------

export const webinarSeries = pgTable(
  "webinar_series",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    title: text("title").notNull(),
    description: text("description"),
    slug: text("slug").notNull(),
    landingPageId: uuid("landing_page_id"),           // FK to pages
    settings: jsonb("settings"),                      // {requireSequentialProgress, certificateEnabled}
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex("idx_webinar_series_slug").on(table.tenantId, table.slug),
    index("idx_webinar_series_tenant").on(table.tenantId),
  ],
);

// ---------------------------------------------------------------------------
// Webinar Series Progress — Per-contact, per-webinar completion tracking
// ---------------------------------------------------------------------------

export const webinarSeriesProgress = pgTable(
  "webinar_series_progress",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    seriesId: uuid("series_id").notNull(),            // FK to webinar_series
    contactId: uuid("contact_id").notNull(),          // FK to persons
    webinarId: uuid("webinar_id").notNull(),          // FK to webinars
    completedAt: timestamp("completed_at", tz),
    watchPercent: integer("watch_percent").notNull().default(0),
  },
  (table) => [
    uniqueIndex("idx_series_progress_unique").on(table.seriesId, table.contactId, table.webinarId),
    index("idx_series_progress_contact").on(table.contactId),
    index("idx_series_progress_tenant").on(table.tenantId),
  ],
);

// ---------------------------------------------------------------------------
// Webinar Questions — Q&A (separate from chat, supports upvoting)
// ---------------------------------------------------------------------------

export const webinarQuestions = pgTable(
  "webinar_questions",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    webinarId: uuid("webinar_id").notNull(),
    contactId: uuid("contact_id").notNull(),
    question: text("question").notNull(),
    answer: text("answer"),
    answeredBy: uuid("answered_by"),
    answeredAt: timestamp("answered_at", tz),
    upvotes: integer("upvotes").notNull().default(0),
    isPinned: boolean("is_pinned").notNull().default(false),
    isHighlighted: boolean("is_highlighted").notNull().default(false),
    aiSuggestedAnswer: text("ai_suggested_answer"),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_webinar_questions_webinar").on(table.webinarId, table.createdAt),
    index("idx_webinar_questions_tenant").on(table.tenantId),
    check("chk_questions_upvotes", sql`${table.upvotes} >= 0`),
  ],
);

// ---------------------------------------------------------------------------
// Webinar Question Upvotes — Prevent double-voting
// ---------------------------------------------------------------------------

export const webinarQuestionUpvotes = pgTable(
  "webinar_question_upvotes",
  {
    tenantId: uuid("tenant_id").notNull(),
    questionId: uuid("question_id").notNull(),
    contactId: uuid("contact_id").notNull(),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex("idx_question_upvote_unique").on(table.questionId, table.contactId),
    index("idx_question_upvotes_tenant").on(table.tenantId),
  ],
);

// ---------------------------------------------------------------------------
// Webinar Reactions — Aggregated in 5-second buckets
// ---------------------------------------------------------------------------

export const webinarReactions = pgTable(
  "webinar_reactions",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    webinarId: uuid("webinar_id").notNull(),
    intervalStart: timestamp("interval_start", tz).notNull(),
    reactionCounts: jsonb("reaction_counts").notNull(), // {"fire": 12, "clap": 8, "heart": 3}
  },
  (table) => [
    uniqueIndex("idx_reactions_webinar_interval").on(table.webinarId, table.intervalStart),
    index("idx_reactions_tenant").on(table.tenantId),
  ],
);

// ---------------------------------------------------------------------------
// Webinar Schedule Slots — For evergreen/recurring webinars
// ---------------------------------------------------------------------------

export const webinarScheduleSlots = pgTable(
  "webinar_schedule_slots",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    webinarId: uuid("webinar_id").notNull(),
    dayOfWeek: integer("day_of_week"),                // 0-6 (null for specific date)
    specificDate: date("specific_date"),               // null for recurring
    startTime: time("start_time").notNull(),
    timezone: text("timezone").notNull(),
    isActive: boolean("is_active").notNull().default(true),
    maxRegistrants: integer("max_registrants"),
  },
  (table) => [
    index("idx_schedule_slots_webinar").on(table.webinarId),
    index("idx_schedule_slots_tenant").on(table.tenantId),
    check(
      "chk_schedule_day_or_date",
      sql`(${table.dayOfWeek} IS NOT NULL AND ${table.specificDate} IS NULL) OR (${table.dayOfWeek} IS NULL AND ${table.specificDate} IS NOT NULL)`,
    ),
  ],
);

// ---------------------------------------------------------------------------
// Webinar Attendee Events — Granular timeline for engagement graph
// ---------------------------------------------------------------------------

export const webinarAttendeeEvents = pgTable(
  "webinar_attendee_events",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    webinarId: uuid("webinar_id").notNull(),
    contactId: uuid("contact_id").notNull(),
    eventType: text("event_type").notNull(),
      // Browser telemetry plus workflow-handoff signals emitted from attendance routes.
    occurredAt: timestamp("occurred_at", tz).notNull(),
    metadata: jsonb("metadata"),
  },
  (table) => [
    index("idx_attendee_events_webinar").on(table.webinarId, table.occurredAt),
    index("idx_attendee_events_contact").on(table.contactId),
    index("idx_attendee_events_tenant").on(table.tenantId),
    check(
      "chk_attendee_event_type",
      sql`${table.eventType} IN ('focus_lost', 'focus_gained', 'reaction', 'chat', 'poll_response', 'cta_click', 'question', 'handout_download')`,
    ),
  ],
);

// ---------------------------------------------------------------------------
// Webinar Reports — persisted post-session intelligence read model
// ---------------------------------------------------------------------------

export const webinarReports = pgTable(
  "webinar_reports",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    webinarId: uuid("webinar_id").notNull(),
    reportData: jsonb("report_data").$type<WebinarReportPayload>().notNull(),
    generatedAt: timestamp("generated_at", tz).notNull().defaultNow(),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex("uq_webinar_reports_webinar").on(table.webinarId),
    index("idx_webinar_reports_tenant").on(table.tenantId),
    index("idx_webinar_reports_generated").on(table.tenantId, table.generatedAt),
  ],
);
